import axiosInstance from '../api/axiosInstance';
import { API_ENDPOINTS } from '../api/config';

export interface VitalOverview {
  vital: string;
  status: 'normal' | 'elevated' | 'low' | 'concerning';
  detail: string;
}

export interface RiskFlag {
  level: 'high' | 'medium' | 'low';
  flag: string;
  recommendation: string;
}

export interface Recommendation {
  category: 'diet' | 'exercise' | 'lifestyle' | 'medical' | 'monitoring';
  recommendation: string;
  priority: 'high' | 'medium' | 'low';
}

export interface DocumentInsight {
  source: string;
  insight: string;
}

export interface HealthSummaryData {
  id: number;
  health_score: number;
  health_score_label: string;
  vitals_summary: {
    overview?: VitalOverview[];
    raw?: Record<string, any>;
  };
  risk_flags: RiskFlag[];
  medications_summary: {
    active?: Array<{ name: string; type: string; dosage: string }>;
    recent?: Array<{ name: string; type: string; dosage: string }>;
  };
  activity_summary: {
    wearable?: Record<string, any>;
    positive_notes?: string[];
  };
  medical_history_summary: {
    conditions?: Array<{ name: string; status: string }>;
    allergies?: Array<{ name: string; type: string; severity: string }>;
    surgeries?: Array<{ name: string; date: string }>;
  };
  document_insights: DocumentInsight[];
  ai_summary: string;
  ai_recommendations: Recommendation[];
  data_sources: string[];
  generation_status: 'pending' | 'processing' | 'completed' | 'failed';
  error_message: string;
  created_at: string;
  updated_at: string;
  date_range_start: string | null;
  date_range_end: string | null;
  raw_health_data: Record<string, any> | null;
}

export interface HealthSummaryListItem {
  id: number;
  health_score: number;
  health_score_label: string;
  generation_status: string;
  data_sources: string[];
  created_at: string;
}

const healthSummaryService = {
  generateSummary: async (startDate?: string, endDate?: string): Promise<HealthSummaryData> => {
    const payload: Record<string, string> = {};
    if (startDate) payload.start_date = startDate;
    if (endDate) payload.end_date = endDate;
    const response = await axiosInstance.post(API_ENDPOINTS.healthSummary.generate, payload);
    return response.data;
  },

  getLatestSummary: async (): Promise<HealthSummaryData> => {
    const response = await axiosInstance.get(API_ENDPOINTS.healthSummary.latest);
    return response.data;
  },

  getSummaryDetail: async (id: number): Promise<HealthSummaryData> => {
    const response = await axiosInstance.get(API_ENDPOINTS.healthSummary.detail(id));
    return response.data;
  },

  getSummaryHistory: async (): Promise<HealthSummaryListItem[]> => {
    const response = await axiosInstance.get(API_ENDPOINTS.healthSummary.history);
    const d = response.data; return Array.isArray(d) ? d : d.results || [];
  },
};

export default healthSummaryService;
